// src/components/questionnaire/DynamicQuestionnaireForm.jsx
/* ------------------------------------------------------------------ */
/* DynamicQuestionnaireForm – runtime-driven multi-step assessment     */
/* ------------------------------------------------------------------ */
/**
 * Flow
 * ----
 * 1. **Load definition** – pulls an array of questions from
 *    `utils/questionnaire/questions/{name}.js` via `loadQuestionnaire()`.
 * 2. **Filter** – drops questions whose `condition(formData[, user])` returns
 *    `false` (`filterQuestions()`).
 * 3. **Build steps** – `buildSteps()` converts the flat list into renderable
 *    *steps*:
 *        • `single`  – one question<br/>
 *        • `group`   – consecutive questions sharing the same `group` key<br/>
 *        • `review`  – read-only summary before submission (auto-appended)
 * 4. **Prefill** – `prefillRangeDefaults()` seeds untouched `range` controls
 *    with their `min` value so validation doesn’t block the user.
 * 5. **Submit** – on the final step the user’s answers are posted to the
 *    regional backend route:<br/>
 *    `/v1/app/questionnaires/{name}/responses`
 *
 * @component
 * @returns {JSX.Element}
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";

import { useAuth } from "../../context/AuthContext";
import { useRegionalBackend } from "../../context/RegionalBackendContext";
import {
  loadQuestionnaire,
  filterQuestions,
  buildSteps,
  prefillRangeDefaults,
} from "../../utils/questionnaireQuestions/questionnaireRuntime";

import { isQuestionValid } from "../../utils/form/isQuestionValid";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { renderStep } from "../../utils/questionnaireQuestions/renderStep";
import { uploadResponses } from "../../utils/questionnaireQuestions/uploadResponses";
import {
  buildQuestionnaireReviewSnapshot,
  resolveQuestionnaireMetadataText,
} from "../../utils/questionnaireQuestions/reviewSnapshot";
import { APP_VERSION } from "../../utils/version";
import { useTranslation } from "react-i18next";
import FormNavButtons from "../form/FormNavButtons";

/* ------------------------------------------------------------------ */
/* React component                                                    */
/* ------------------------------------------------------------------ */
function normalizeLoadedQuestionnaire(loadedQuestionnaire) {
  if (Array.isArray(loadedQuestionnaire)) {
    return {
      questions: loadedQuestionnaire,
      metadata:
        loadedQuestionnaire.metadata &&
        typeof loadedQuestionnaire.metadata === "object"
          ? loadedQuestionnaire.metadata
          : null,
    };
  }

  return {
    questions: [],
    metadata: null,
  };
}

function DynamicQuestionnaireForm({ onSubmitted = () => {} }) {
  const { user } = useAuth();
  const { profile } = useRegionalBackend();
  const { questionnaireName } = useParams();

  const translationForm = questionnaireName;
  const { t, i18n } = useTranslation(questionnaireName); // form-specific translations
  const { t: tCommon } = useTranslation(); // default/common translations
  const questionnaireLocale = i18n.resolvedLanguage || i18n.language || "en-GB";

  const navigate = useNavigate();

  /* ---------------- State ------------------------------------------ */
  const [questions, setQuestions] = useState([]);
  const [questionnaireMetadata, setQuestionnaireMetadata] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous guard against a duplicate submission. State updates are async,
  // so a fast double-click (or Enter + click) can fire `goNext` twice before a
  // re-render disables the button; this ref is flipped synchronously so the
  // second call bails out before a duplicate response is ever created.
  const submittingRef = useRef(false);

  /* ---------------- Load questionnaire ----------------------------- */
  useEffect(() => {
    setQuestions([]);
    setQuestionnaireMetadata(null);
    setFormData({});
    setCurrentStep(0);
    setIsSubmitting(false);
    submittingRef.current = false;

    loadQuestionnaire(questionnaireName)
      .then((loadedQuestionnaire) => {
        const normalized = normalizeLoadedQuestionnaire(loadedQuestionnaire);
        setQuestions(normalized.questions);
        setQuestionnaireMetadata(normalized.metadata);
      })
      .catch((err) => {
        console.error("Failed to load questionnaire:", err);
        setQuestions([]);
        setQuestionnaireMetadata(null);
      });
  }, [questionnaireName]);

  /* ---------------- Derived values -------------------------------- */
  const questionnaireContext = useMemo(
    () => ({
      ...(user && typeof user === "object" ? user : {}),
      profile,
    }),
    [profile, user],
  );
  const filteredQuestions = useMemo(
    () => filterQuestions(questions, formData, questionnaireContext),
    [questions, formData, questionnaireContext],
  );

  const steps = useMemo(
    () => buildSteps(filteredQuestions),
    [filteredQuestions],
  );
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;
  const questionnaireTitle = useMemo(
    () =>
      resolveQuestionnaireMetadataText(
        questionnaireMetadata,
        "titleKey",
        "title",
        t,
      ),
    [questionnaireMetadata, t],
  );
  const questionnaireIntro = useMemo(
    () =>
      resolveQuestionnaireMetadataText(
        questionnaireMetadata,
        "introKey",
        "intro",
        t,
      ),
    [questionnaireMetadata, t],
  );
  const questionnaireDisclaimer = useMemo(
    () =>
      resolveQuestionnaireMetadataText(
        questionnaireMetadata,
        "disclaimerKey",
        "disclaimer",
        t,
        tCommon,
      ),
    [questionnaireMetadata, t, tCommon],
  );
  const reviewSnapshot = useMemo(
    () =>
      buildQuestionnaireReviewSnapshot({
        questionnaireId: questionnaireName,
        questionnaireMetadata,
        questions: filteredQuestions,
        formData,
        questionnaireTranslate: t,
        commonTranslate: tCommon,
      }),
    [
      filteredQuestions,
      formData,
      questionnaireMetadata,
      questionnaireName,
      t,
      tCommon,
    ],
  );

  /* ---------------- Prefill range defaults ------------------------ */
  useEffect(() => {
    const defaults = prefillRangeDefaults(filteredQuestions, formData);
    if (Object.keys(defaults).length) {
      setFormData((prev) => ({ ...prev, ...defaults }));
    }
  }, [filteredQuestions, formData]);

  /* ---------------- Handlers -------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const goPrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const goNext = async (e) => {
    e?.preventDefault();

    /* ---------- Final step → upload & redirect -------------------- */
    if (currentStep === totalSteps - 1) {
      // Ignore repeat presses while a submission is already in flight.
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);

      try {
        await uploadResponses(questionnaireName, formData, {
          locale: questionnaireLocale,
          contentVersion:
            questionnaireMetadata?.contentVersion || APP_VERSION,
          reviewSnapshot,
        });
        // The host owns where a finished questionnaire leads, since only it
        // knows what else is still outstanding. Without an answer, fall back to
        // the list this form was opened from.
        let destination = null;
        try {
          destination = onSubmitted(questionnaireName);
        } catch (callbackError) {
          console.error("Questionnaire completion refresh failed:", callbackError);
        }
        navigate(typeof destination === "string" ? destination : "/home");
      } catch (err) {
        console.error("Questionnaire submission failed:", err);
        // Re-enable the button so the user can retry after a failed save.
        submittingRef.current = false;
        setIsSubmitting(false);
        alert(tCommon("form.failedSaveAlert"));
      }
      return;
    }

    /* ---------- Validate current step ----------------------------- */
    const step = steps[currentStep];
    const invalid =
      step.type === "single"
        ? !isQuestionValid(step.question, formData)
        : step.questions.some((q) => !isQuestionValid(q, formData));

    if (invalid) {
      alert(tCommon("form.reqFieldsAlert"));
      return;
    }

    setCurrentStep((s) => s + 1);
  };

  /* ---------------- Render ---------------------------------------- */
  return (
    <div className="user-form w-full h-fit flex flex-col gap-4">
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: getPrimaryColour() },
        }}
      />

      {questionnaireTitle ? (
        <div className="flex flex-col gap-2">
          <p className="title-text text-center whitespace-pre-line">
            {questionnaireTitle}
          </p>
          {currentStep === 0 && questionnaireIntro ? (
            <p className="text-start whitespace-pre-line">
              {questionnaireIntro}
            </p>
          ) : null}
          {currentStep === 0 && questionnaireDisclaimer ? (
            <p className="muted-text text-start whitespace-pre-line">
              {questionnaireDisclaimer}
            </p>
          ) : null}
        </div>
      ) : questionnaireName === "r-scale" ? (
        <div className="flex flex-col gap-2">
          <p className="title-text text-center whitespace-pre-line">
            {t("r-scale.title")}
          </p>
          {currentStep === 0 && (
            <p className=" text-start whitespace-pre-line">
              {t("r-scale.intro")}
            </p>
          )}
        </div>
      ) : questionnaireName === "sgrq" ? (
        <div className="flex flex-col gap-2">
          <p className="title-text text-center whitespace-pre-line">
            {t("sgrq.title")}
          </p>{" "}
          {currentStep === 0 && (
            <p className=" text-start whitespace-pre-line">{t("sgrq.intro")}</p>
          )}
          {currentStep === 0 && (
            <p className="muted-text text-start whitespace-pre-line">
              {t("sgrq.disclaimer")}
            </p>
          )}
        </div>
      ) : null}

      {renderStep(
        steps,
        currentStep,
        formData,
        handleChange,
        goNext,
        filteredQuestions,
        translationForm,
      )}

      {/* <div className="flex justify-between">
        {currentStep > 0 ? (
          <button onClick={goPrev} className="outlined-button">
            Previous
          </button>
        ) : (
          <div />
        )}
        <button onClick={goNext} className="primary-button">
          {currentStep === totalSteps - 1 ? "Confirm" : "Next"}
        </button> */}

      <FormNavButtons
        hasPrev={currentStep > 0}
        onPrev={goPrev}
        onNext={goNext}
        isLast={currentStep === totalSteps - 1}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export default DynamicQuestionnaireForm;
